import DOMPurify from 'dompurify';
import xss from 'xss';

// Configure DOMPurify
const purifyConfig = {
  ALLOWED_TAGS: [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'b', 'i', 'strong', 'em',
    'ul', 'ol', 'li',
    'blockquote', 'pre', 'code',
    'a', 'img',
    'table', 'thead', 'tbody', 'tr', 'th', 'td'
  ],
  ALLOWED_ATTR: [
    'href', 'src', 'alt', 'title', 'class',
    'target', 'rel', 'id', 'name'
  ],
  ALLOW_DATA_ATTR: false,
  USE_PROFILES: { html: true }
};

// Configure XSS
const xssOptions = {
  whiteList: {
    a: ['href', 'title', 'target', 'rel'],
    img: ['src', 'alt', 'title'],
    ...Object.fromEntries(
      purifyConfig.ALLOWED_TAGS
        .filter(tag => !['a', 'img'].includes(tag))
        .map(tag => [tag, []])
    )
  },
  stripIgnoreTag: true,
  stripIgnoreTagBody: ['script', 'style', 'xml']
};

/**
 * Sanitize HTML content
 */
export function sanitizeHTML(content: string): string {
  if (!content) return '';

  // First pass: DOMPurify
  let sanitized = DOMPurify.sanitize(content, purifyConfig);

  // Second pass: XSS filter
  sanitized = xss(sanitized, xssOptions);

  return sanitized;
}

/**
 * Sanitize plain text
 */
export function sanitizeText(text: string): string {
  if (!text) return '';

  // Remove any HTML tags
  text = text.replace(/<[^>]*>/g, '');

  // Encode special characters
  text = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  // Remove potential script injections
  text = text.replace(/javascript:/gi, '')
    .replace(/data:/gi, '')
    .replace(/vbscript:/gi, '');

  return text.trim();
}

/**
 * Validate and sanitize URLs
 */
export function sanitizeURL(url: string): string {
  if (!url) return '';

  try {
    const parsed = new URL(url);
    
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return '';
    }

    return parsed.href;
  } catch {
    return '';
  }
}

/**
 * Log security events
 */
export async function logSecurityEvent(
  eventType: string,
  details: Record<string, any> = {},
  userId?: string
): Promise<void> {
  try {
    const { supabase } = await import('./supabase');
    
    // Mask sensitive data
    if (details.password) {
      details.password = '[REDACTED]';
    }
    
    if (details.ip) {
      details.ip = '[MASKED]';
    }
    
    // Add timestamp
    details.timestamp = new Date().toISOString();
    
    // Add user agent
    if (typeof window !== 'undefined') {
      details.userAgent = window.navigator.userAgent;
    }
    
    await supabase.from('security_logs').insert({
      event_type: eventType,
      user_id: userId,
      details: sanitizeText(JSON.stringify(details))
    });
    
    // Also log to console in development
    if (import.meta.env.DEV) {
      console.log(`Security Event: ${eventType}`, details);
    }
  } catch (error) {
    console.error('Error logging security event:', error);
  }
}

/**
 * Detect suspicious activity
 */
export function detectSuspiciousActivity(activity: Record<string, any>): boolean {
  // Check for common attack patterns
  const suspiciousPatterns = [
    /select.*from/i,
    /union.*select/i,
    /insert.*into/i,
    /drop.*table/i,
    /alert\(/i,
    /confirm\(/i,
    /eval\(/i,
    /document\.cookie/i,
    /\.\.\/\.\.\/\.\.\/\.\.\/\.\.\//i,
    /etc\/passwd/i
  ];
  
  // Convert activity to string for pattern matching
  const activityString = JSON.stringify(activity).toLowerCase();
  
  // Check for suspicious patterns
  for (const pattern of suspiciousPatterns) {
    if (pattern.test(activityString)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Generate a secure random token
 */
export function generateSecureToken(length: number = 32): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Hash a password (client-side strengthening only)
 * Note: This is NOT a replacement for proper server-side hashing
 */
export async function strengthenPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}